import { Socket } from "socket.io-client";
import { useState, FormEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { VoteStatus } from "types/voteEnum";
import { QuestionType } from "types/dataVoteType";

import ActionSection from "./ActionSection";
import TitleHeader from "./TitleHeader";

import { EVENT } from "types/socketEventsType";
import encryptAnswers from "components/helper/encryptAnswers";
import { useSession } from "components/helper/sessionContext";

type Props = {
    question: QuestionType;
    id: string;
    useDecimal: boolean;
    minValue: number;
    maxValue: number;
    publicKey: string;
};

const emitAsyncAnswer = async (
    socket: Socket,
    args: {
        votingDeviceId: string;
        publicKey: string;
        answers: { questionId: number; answer: string }[]; //no weight needed for roll-call/tests question type
        //generic method makes answering broken
        //Thus, i duplicate it here for our needs
    },
) => {
    let answers: any = args.answers;
    const shouldEncryptAnswer =
        args.publicKey !== null &&
        args.publicKey !== undefined &&
        args.publicKey !== "";

    if (shouldEncryptAnswer) {
        answers = await encryptAnswers(args.publicKey, args.answers);
    }

    return new Promise<{ [key: string]: any }>((resolve, reject) => {
        socket.emit(
            EVENT.ANSWER,
            {
                votingDeviceId: args.votingDeviceId,
                answers: answers,
            },
            (res: any) => {
                if ("SUCCESS" === res.status) {
                    resolve(res.data);
                } else {
                    reject({ code: res.code, message: res.message });
                }
            },
        );
    });
};

const NumericQuestion = ({
    question,
    id,
    useDecimal,
    minValue,
    maxValue,
    publicKey,
}: Props) => {
    const [answer, setAnswer] = useState<string>("");
    const [voteStatus, setVoteStatus] = useState<string>(VoteStatus.Idle);

    const { t } = useTranslation();
    const { socket } = useSession();

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (answer.length >= 1) {
            handleValidate();
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const re = /^\d+$/;
        const { value } = e.currentTarget;
        const ok = re.exec(value);

        if (!useDecimal && !ok) {
            e.currentTarget.value = "";
            setAnswer("");
            return;
        }

        setAnswer(value);
        setVoteStatus(VoteStatus.PendingValidation);
        if (0 === value?.length) {
            setVoteStatus(VoteStatus.Idle);
        }
    };

    const handleValidate = async () => {
        setVoteStatus(VoteStatus.Loading);

        try {
            await onValidate(answer);
            setVoteStatus(VoteStatus.Success);
        } catch {
            setVoteStatus(VoteStatus.Error);
        }
    };

    const handleReset = () => {
        setAnswer("");
        setVoteStatus(VoteStatus.Idle);
    };
    const handlePressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") {
            return;
        }

        e.preventDefault();

        const answerIsNumber = !Number.isNaN(Number.parseFloat(answer));
        if (answerIsNumber) {
            handleSubmit(e as any);
        }
    };

    const onValidate = (answer: string) => {
        if (!socket) return Promise.reject(new Error("No socket"));

        return emitAsyncAnswer(socket, {
            votingDeviceId: id as string,
            publicKey: publicKey,
            answers: [
                {
                    questionId: question?.id as number,
                    answer,
                } as any,
            ],
        });
    };
    return (
        <WrapperVote>
            <TitleHeader title={question.title} />
            <WrapperForm>
                <form onSubmit={handleSubmit} autoComplete="off">
                    <StyledInput
                        value={answer}
                        onChange={handleChange}
                        onKeyDown={handlePressEnter}
                        type={"number"}
                        maxLength={30}
                        placeholder={t("enterNumber")}
                        min={minValue ? minValue : undefined}
                        max={maxValue ? maxValue : undefined}
                        step={useDecimal ? 0.1 : 1}
                    />
                </form>
            </WrapperForm>
            <ActionSection
                voteStatus={voteStatus}
                handleValidate={handleValidate}
                handleReset={handleReset}
            />
        </WrapperVote>
    );
};

const WrapperVote = styled.div`
    width: 100%;
    max-width: 620px;
    margin: auto;
    @media (max-width: 768px) {
        max-width: 350px;
    }
`;

const WrapperForm = styled.div`
    text-align: center;
    margin-top: 1.5rem;
`;

const StyledInput = styled.input`
    margin: auto;
    height: 35px;
    padding: 6px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 15px;
    width: 100%;
    box-sizing: border-box;
    &[disabled] {
        background: #e8e8e8;
        opacity: 0.7;
        cursor: not-allowed;
    }
    :focus-visible {
        outline: solid 1px #010203;
    }
`;

export default NumericQuestion;
